import React, {useId, useMemo, useState} from 'react';

import {WidthItem} from 'src/components/width-item';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';

import {FieldDescription, Types as FieldDescriptionTypes} from '@pb/kit-component-field-description';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import Loading, {Types} from '@pb/kit-component-loading';


type Props = {
    id?: string,
    value: string,
    onChange?: (value: string) => void,
    onBlur?: (value: string) => void,

    label?: React.ReactNode,
    description?: string,
    errorDescription?: string,

    maxLength?: number,
    width?: number | string,
    placeholder?: string,
    autofocus?: boolean,
    validation?: (text: string) => boolean,

    readOnly?: boolean,
    disabled?: boolean,

    busy?: boolean,
    valid?: boolean,
    onWarningClick?: () => void,
    blurNotChanged?: boolean,
};

export const FormInput = (props: Props) => {
    const {
        value,
        onBlur,

        label,
        description,
        errorDescription,

        maxLength,
        width,
        placeholder,
        autofocus,
        onWarningClick,
        onChange,
        validation,

        readOnly,
        disabled,
        blurNotChanged,
        valid,
        busy,
    } = props;

    const id = useId();
    const labelId = useId();
    const descriptionId = useId();

    const [internalValue, setInternalValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);

    useEffectWithDeps((prev, current) => {
        if (prev !== current) {
            setInternalValue(current);
        }
    }, value);

    const color = useMemo(() => {
        if (!busy && (!isFocused || !!onChange) && !readOnly) {
            if (valid !== undefined && !valid) {
                return 'ERROR';
            } else if (valid) {
                return 'SUCCESS';
            }
        }

        return undefined;
    }, [valid, busy, isFocused, readOnly, onChange]);

    const endAdornment = useMemo(() => {
        if ((!isFocused || !!onChange) && !busy && !readOnly) {
            if (valid !== undefined && !valid) {
                return <Icon type={Icons.ERROR} size="Size_14" className="error-color" onClick={onWarningClick}/>;
            } else if (valid) {
                return <Icon type={Icons.CHECK} size="Size_14" className="success-color"/>;
            }
        }

        if (readOnly) {
            return <Icon type={Icons.LOCK} size="Size_14" color={color}/>;
        }

        if (busy) {
            return <Loading type={Types.ICON} fast={true}/>;
        }

        return undefined;
    }, [valid, readOnly, color, busy, isFocused, onChange, onWarningClick]);

    return (
        <WidthItem width={width}>
            {!!label && (
                <Label id={labelId} htmlFor={props.id || id}>
                    {label}
                </Label>
            )}
            <Input
                id={props.id || id}
                value={internalValue || ''}

                onInput={event => {
                    const newValue = event.currentTarget.value;
                    if (validation) {
                        const isValid = validation(newValue);

                        if (!isValid) {
                            return;
                        }
                    }

                    setInternalValue(event.currentTarget.value);
                    if (onChange) {
                        onChange(event.currentTarget.value.trim());
                    }
                }}
                onKeyUp={event => {
                    if (event.key === 'Enter') {
                        event.currentTarget.blur();
                    }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    if (!blurNotChanged && internalValue === value) {
                        setIsFocused(false);
                        return;
                    }

                    if (!readOnly && onBlur) {
                        onBlur(internalValue.trim());
                    }
                    setIsFocused(false);
                }}

                autoComplete="off"
                autoCorrect="off"
                autoFocus={autofocus}
                aria-labelledby={label ? labelId : undefined}
                aria-describedby={description ? descriptionId : undefined}
                aria-invalid={valid !== undefined && !valid}

                color={color}
                maxLength={maxLength}
                placeholder={placeholder}
                endAdornment={endAdornment}
                disabled={disabled}
                readOnly={readOnly}
            />
            {!!description && !errorDescription && (
                <FieldDescription aria-live="polite" id={descriptionId} color={FieldDescriptionTypes.DEFAULT}>
                    {description}
                </FieldDescription>
            )}
            {!!errorDescription && (!!onChange || !isFocused) && !busy && (
                <FieldDescription aria-live="polite" id={descriptionId} color={FieldDescriptionTypes.ERROR}>
                    {errorDescription}
                </FieldDescription>
            )}
        </WidthItem>
    );
};
